import { Offer } from 'common/contracts';
import { z } from 'zod';

import {
  MIN_USD_PRICE,
  TRIMMED_INPUT_REGEX
} from '../../../constants/constants';
import {
  EPopupSubType,
  ESaleDiscountDisplayType,
  OfferType
} from '../../../constants/enums';

export const checkUniqueness = ({
  value,
  field,
  offers,
  isEdit,
  currentValue
}: {
  value: string;
  field: keyof Offer;
  offers: Offer[];
  isEdit: boolean;
  currentValue?: string;
}) => {
  if (!value) return true;
  return !offers.some((o) => {
    if (!isEdit) return o[field] === value;
    return o[field] === value && currentValue !== value;
  });
};

export const DEFAULT_OFFER_DESIGN_EXTERNAL_ID = 'Default_PopUp';

export const POPUPS_FORM_SALE_TYPE_OPTIONS = [
  {
    value: ESaleDiscountDisplayType.PERCENTAGE,
    label: 'Percentage'
  },
  {
    value: ESaleDiscountDisplayType.MULTIPLIER,
    label: 'Multiplier'
  },
  {
    value: ESaleDiscountDisplayType.FIXED_AMOUNT,
    label: 'Fixed amount'
  }
];

export enum EPopupTriggersAction {
  PURCHASE_COMPLETED = 'purchaseCompleted'
}

export const getPriorityOptions = () =>
  Array.from({ length: 10 }, (_, i) => {
    const value = i + 1;
    return {
      value,
      label: value.toString()
    };
  });

export const FORM_SCHEMA = z
  .object({
    _id: z.string().optional(),
    active: z.boolean(),
    triggersAction: z.nativeEnum(EPopupTriggersAction),
    triggersFrequency: z.number().min(1),
    triggersOfferExternalIds: z.array(z.string()).optional(),
    name: z
      .string()
      .min(1, 'Name is required')
      .refine((value) => TRIMMED_INPUT_REGEX.test(value), {
        message: 'Name should not be spaces'
      }),
    displayName: z.string().optional(),
    subType: z.nativeEnum(EPopupSubType),
    description: z
      .string()
      .optional()
      .refine((value) => !value || value.length >= 3, {
        message: 'Description length should be at least 3 characters',
        path: ['description']
      }),
    publisherOfferId: z
      .string()
      .min(1, 'External ID is required')
      .refine((value) => TRIMMED_INPUT_REGEX.test(value), {
        message: 'External ID should not be spaces'
      }),
    offerUiId: z.string().min(1, 'Offer Design is required'),
    priority: z.number().min(1),
    price: z.number(),
    setAsFree: z.boolean(),
    startOver: z.boolean(),
    segments: z.array(z.string()),
    tags: z.array(z.string()),
    type: z.nativeEnum(OfferType),
    productName: z.string().optional(),
    quantity: z.number(),
    products: z.array(z.any()).optional(),
    discount: z
      .number()
      .min(0, { message: 'Price discount cannot be negative' })
      .max(99, { message: 'Price discount cannot be more than 99%' })
      .refine((value) => /^\d+(\.\d{1})?$/.test(value.toString()), {
        message: 'Price discount can have one decimal place at most',
        path: ['discount']
      }),
    priceDiscountType: z.nativeEnum(ESaleDiscountDisplayType)
  })
  .refine(
    (data) =>
      data.subType === EPopupSubType.DAILY_BONUS ||
      data.setAsFree ||
      data.price >= MIN_USD_PRICE,
    {
      path: ['price'],
      message: `Price must be at least ${MIN_USD_PRICE}`
    }
  );

export type PopupsFormFields = z.infer<typeof FORM_SCHEMA>;

export const DEFAULT_VALUES = {
  active: true,
  name: '',
  displayName: '',
  triggersAction: EPopupTriggersAction.PURCHASE_COMPLETED,
  triggersFrequency: 1,
  triggersOfferExternalIds: [],
  subType: EPopupSubType.DAILY_BONUS,
  description: '',
  publisherOfferId: '',
  offerUiId: '',
  priority: 1,
  price: 0,
  setAsFree: false,
  startOver: true,
  segments: [],
  tags: [],
  type: OfferType.POPUP,
  productName: '',
  quantity: 1,
  products: [],
  discount: 0,
  priceDiscountType: ESaleDiscountDisplayType.PERCENTAGE
};
